import { combineReducers, configureStore, Middleware } from "@reduxjs/toolkit";
import LogRocket from "logrocket";
import {
  accessibleFarmerDashboardsReducer,
  accessibleUserReducer,
  animalGroupReducer,
  appReducer,
  arableFieldReducer,
  defaultStockClassesReducer,
  deviceReducer,
  diaryEntryReducer,
  farmerImageUploadReducer,
  farmerProgrammeDashboardsReducer,
  farmsReducer,
  feedEventReducer,
  feedPurchaseEventReducer,
  feedStockReducer,
  fertilizerEventReducer,
  fetchToken,
  fetchUserById,
  fieldPreparationEventReducer,
  fieldReducer,
  fileEntriesReducer,
  fileUploadReducer,
  mapReducer,
  metabaseUrlReducer,
  programmeActionsQuestionnaireFilledOutIdsReducer,
  programmeActionsReducer,
  projectDashboardsReducer,
  questionnaireStartedReducer,
  quickAssessmentStartedReducer,
  readingsReducer,
  reseedEventReducer,
  selectedAccessibleFarmerDashboardReducer,
  selectedAccessibleFarmerReducer,
  selectedFarmerDashboardReducer,
  selectedProjectDashboardReducer,
  stockClassBirthingPeriodReducer,
  stockClassFeedAllocationReducer,
  stockClassReducer,
  userReducer,
  usersWithFileAccessReducer,
  yieldEventReducer,
} from "model";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { getLogRocketReduxMiddlewareConfig } from "../common/analytics/logRocketOptions";
import { quickAssessmentResultsReducer } from "./quickAssessmentSlice";

const combinedReducer = combineReducers({
  app: appReducer,
  user: userReducer,
  farms: farmsReducer,
  fields: fieldReducer,
  arableFields: arableFieldReducer,
  farmerImageUploads: farmerImageUploadReducer,
  diaryEntries: diaryEntryReducer,
  reseedEvents: reseedEventReducer,
  map: mapReducer,
  animalGroups: animalGroupReducer,
  stockClasses: stockClassReducer,
  readings: readingsReducer,
  defaultStockClasses: defaultStockClassesReducer,
  stockClassBirthingPeriods: stockClassBirthingPeriodReducer,
  stockClassFeedAllocations: stockClassFeedAllocationReducer,
  feedStock: feedStockReducer,
  feedPurchaseEvents: feedPurchaseEventReducer,
  fieldPreparationEvents: fieldPreparationEventReducer,
  feedEvents: feedEventReducer,
  yieldEvents: yieldEventReducer,
  fertilizerEvents: fertilizerEventReducer,
  metabaseUrlEntries: metabaseUrlReducer,
  accessibleUsers: accessibleUserReducer,
  fileUploads: fileUploadReducer,
  fileEntries: fileEntriesReducer,
  usersWithFileAccess: usersWithFileAccessReducer,
  device: deviceReducer,
  farmerProgrammeDashboards: farmerProgrammeDashboardsReducer,
  selectedFarmerDashboard: selectedFarmerDashboardReducer,
  projectDashboards: projectDashboardsReducer,
  selectedProjectDashboard: selectedProjectDashboardReducer,
  accessibleFarmerDashboards: accessibleFarmerDashboardsReducer,
  selectedAccessibleFarmerDashboard: selectedAccessibleFarmerDashboardReducer,
  selectedAccessibleFarmer: selectedAccessibleFarmerReducer,
  programmeActions: programmeActionsReducer,
  quickAssessmentResults: quickAssessmentResultsReducer,
  programmeActionsQuestionnaireFilledOutIds:
    programmeActionsQuestionnaireFilledOutIdsReducer,
  questionnaireStarted: questionnaireStartedReducer,
  quickAssessmentStarted: quickAssessmentStartedReducer,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === "RESET_STORE") {
    state = undefined;
  }

  return combinedReducer(state, action);
};

function getLogRocketMiddleware() {
  return LogRocket.reduxMiddleware(
    getLogRocketReduxMiddlewareConfig(
      fetchToken.fulfilled.type,
      fetchUserById.fulfilled.type
    )
  ) as Middleware<{}, RootState>; // eslint-disable-line @typescript-eslint/ban-types
}

const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActionPaths: ["feedEvents.entities", "payload.timestamp"],
        // Ignore these paths in the state
        ignoredPaths: ["items.dates"],
      },
    }).concat(getLogRocketMiddleware()),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;

export function useAppDispatch(): AppDispatch {
  return useDispatch<AppDispatch>();
}

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
